/**
 * P0 Command Centre — Dashboard Overview
 * ─────────────────────────────────────────────────────────────
 * Renders 9 widgets against `GET /api/dashboard/overview`.
 * Strict "Tactical Industrial" aesthetic per `/app/design_guidelines.json`:
 *   - Chivo headings, IBM Plex Sans body, JetBrains Mono numbers
 *   - Sharp 1px slate borders, no rounded-2xl, no gradients, no shadow blur
 *   - Signal severity via border-l-4 (emerald/blue/amber/rose/slate-900)
 *   - Sticky solid slate-900 header (command-center feel)
 *
 * Data model: WEBSITE_API_CONTRACTS.md §5 (capacity) + `/api/dashboard/overview`.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Pulse, Warning, ChartLineUp, CurrencyDollar,
  UsersFour, Trophy, Gauge, ClockCounterClockwise, ArrowUpRight,
  ArrowDownRight, Minus, ArrowsClockwise, Hammer, Broadcast,
  Timer, ArrowsSplit, Medal, ClipboardText,
} from '@phosphor-icons/react';

// ─────────────────────────────────────────────────────────────
// Small tokens
// ─────────────────────────────────────────────────────────────
const SIGNAL = {
  healthy:  { border: 'border-emerald-500', bg: 'bg-emerald-50',  text: 'text-emerald-800',  icon: 'text-emerald-600' },
  busy:     { border: 'border-blue-500',    bg: 'bg-blue-50',     text: 'text-blue-800',     icon: 'text-blue-600' },
  warning:  { border: 'border-amber-500',   bg: 'bg-amber-50',    text: 'text-amber-900',    icon: 'text-amber-600' },
  critical: { border: 'border-rose-500',    bg: 'bg-rose-50',     text: 'text-rose-900',     icon: 'text-rose-600' },
  blocked:  { border: 'border-rose-500',    bg: 'bg-slate-900',   text: 'text-rose-400',     icon: 'text-rose-500' },
};

const fmtMoney = (n) => (n == null || Number.isNaN(n)) ? '—' :
  new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', maximumFractionDigits: 0 }).format(n);
const fmtInt   = (n) => (n == null || Number.isNaN(n)) ? '—' : new Intl.NumberFormat('en-NZ').format(n);

// ─────────────────────────────────────────────────────────────
// Shared shell for every widget — tactical panel look
// ─────────────────────────────────────────────────────────────
function WidgetShell({ title, icon: Icon, children, className = '', testid, actionsRight }) {
  return (
    <section
      data-testid={testid}
      className={`bg-white border border-slate-300 p-5 ${className}`}
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} weight="bold" className="text-slate-500" />}
          <h3 className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">{title}</h3>
        </div>
        {actionsRight}
      </header>
      {children}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// 1. Capacity toggle
// ─────────────────────────────────────────────────────────────
function CapacityWidget({ capacity }) {
  const status = capacity?.status || 'open';
  const label = {
    open: 'ACCEPTING LEADS',
    busy: 'BUSY — STILL TAKING',
    at_capacity: 'AT CAPACITY',
    waitlist_only: 'WAITLIST ONLY',
    closed_for_maintenance: 'CLOSED',
  }[status] || 'UNKNOWN';
  const sev = status === 'open' ? 'healthy'
    : status === 'busy' ? 'busy'
    : status === 'at_capacity' ? 'warning'
    : 'critical';
  const s = SIGNAL[sev];

  return (
    <WidgetShell title="Capacity Signal" icon={Broadcast} testid="widget-capacity">
      <div className={`border-l-4 ${s.border} pl-4`} data-testid="capacity-signal-bar">
        <div className="flex items-baseline gap-3 mb-2">
          <span className={`${s.text} font-black tracking-tight text-lg`} style={{ fontFamily: "'Chivo', sans-serif" }} data-testid="capacity-status-label">
            {label}
          </span>
        </div>
        <p className="text-sm text-slate-600 mb-3" data-testid="capacity-message">{capacity?.message}</p>
        <dl className="grid grid-cols-3 gap-x-6 gap-y-1 text-xs">
          <div>
            <dt className="text-slate-500 uppercase tracking-wide">Slots / 4wk</dt>
            <dd className="font-mono text-lg text-slate-900" data-testid="capacity-slots">{fmtInt(capacity?.available_slots_next_4_weeks)}</dd>
          </div>
          <div>
            <dt className="text-slate-500 uppercase tracking-wide">Apprsl days</dt>
            <dd className="font-mono text-lg text-slate-900" data-testid="capacity-turnaround-days">{fmtInt(capacity?.expected_appraisal_turnaround_days)}</dd>
          </div>
          <div>
            <dt className="text-slate-500 uppercase tracking-wide">Start weeks</dt>
            <dd className="font-mono text-lg text-slate-900" data-testid="capacity-work-start-weeks">{fmtInt(capacity?.expected_work_start_weeks)}</dd>
          </div>
        </dl>
      </div>
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 2. Pipeline balance — 11-stage subway
// ─────────────────────────────────────────────────────────────
function PipelineWidget({ pipeline }) {
  const stages = pipeline?.stages || [];
  const max = Math.max(1, ...stages.map((s) => s.count));
  const navigate = useNavigate();

  return (
    <WidgetShell
      title="Pipeline Balance"
      icon={ChartLineUp}
      testid="widget-pipeline"
      actionsRight={<span className="text-xs text-slate-500 font-mono" data-testid="pipeline-total">{fmtInt(pipeline?.total_active)} active</span>}
    >
      <div className="overflow-x-auto -mx-2 px-2 pb-1" data-testid="pipeline-stages">
        <div className="flex gap-1 min-w-full">
          {stages.map((s) => {
            const h = Math.max(4, Math.round(64 * (s.count / max)));
            const hot = s.avg_days_in_stage > 14 && s.count > 0;
            return (
              <button
                key={s.key}
                onClick={() => navigate(`/pipeline?stage=${encodeURIComponent(s.key)}`)}
                className={`flex-1 min-w-[64px] group text-left border border-slate-300 hover:bg-slate-50 transition-none p-2`}
                data-testid={`pipeline-stage-${s.key}`}
              >
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 truncate">{s.label}</div>
                <div className="flex items-end gap-2">
                  <span className="font-mono text-2xl font-semibold text-slate-900 tracking-tighter" data-testid={`pipeline-count-${s.key}`}>{s.count}</span>
                  <div className={`flex-1 self-end ${hot ? 'bg-amber-500' : 'bg-slate-800'}`} style={{ height: `${h}px` }} />
                </div>
                <div className={`text-[10px] mt-1 font-mono ${hot ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>
                  {s.avg_days_in_stage.toFixed(1)}d avg
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 3. Bottleneck alerts
// ─────────────────────────────────────────────────────────────
function BottleneckAlertsWidget({ bottlenecks }) {
  const alerts = bottlenecks?.alerts || [];
  const navigate = useNavigate();
  if (alerts.length === 0) {
    return (
      <WidgetShell title="Bottleneck Alerts" icon={Warning} testid="widget-bottlenecks">
        <div className={`border-l-4 ${SIGNAL.healthy.border} ${SIGNAL.healthy.bg} p-3`}>
          <span className="text-sm text-emerald-800 font-medium">All stages within thresholds. Nothing on fire.</span>
        </div>
      </WidgetShell>
    );
  }
  return (
    <WidgetShell
      title="Bottleneck Alerts"
      icon={Warning}
      testid="widget-bottlenecks"
      actionsRight={<span className="text-xs font-mono text-rose-700" data-testid="bottleneck-count">{bottlenecks.count} live</span>}
    >
      <ul className="divide-y divide-slate-200 -mx-1 max-h-96 overflow-y-auto" data-testid="bottleneck-list">
        {alerts.map((a) => {
          const s = SIGNAL[a.severity] || SIGNAL.warning;
          return (
            <li key={a.key} className={`px-3 py-2 border-l-4 ${s.border} ${s.bg} mb-1`}>
              <button
                onClick={() => a.action_href && navigate(a.action_href)}
                className="w-full flex items-start justify-between gap-3 text-left"
                data-testid={`bottleneck-item-${a.key}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {a.severity === 'critical' && <span className="w-2 h-2 bg-rose-500 rounded-full animate-pulse" />}
                    <div className={`text-sm font-semibold ${s.text} truncate`}>{a.title}</div>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">{a.detail}</div>
                </div>
                <ArrowUpRight size={16} className={s.icon} />
              </button>
            </li>
          );
        })}
      </ul>
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 4. Cashflow snapshot
// ─────────────────────────────────────────────────────────────
function CashflowWidget({ cashflow }) {
  const metrics = [
    { key: 'invoiced_mtd',   label: `Invoiced ${cashflow?.month_label || ''}`, value: cashflow?.invoiced_mtd,   tone: 'text-slate-900' },
    { key: 'collected_mtd',  label: 'Collected MTD',                            value: cashflow?.collected_mtd,  tone: 'text-emerald-700' },
    { key: 'overdue',        label: 'Overdue',                                  value: cashflow?.overdue,        tone: 'text-rose-700' },
    { key: 'pipeline_value', label: 'Pipeline Value',                           value: cashflow?.pipeline_value, tone: 'text-slate-900' },
  ];
  return (
    <WidgetShell title="Cashflow Snapshot" icon={CurrencyDollar} testid="widget-cashflow">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metrics.map((m) => (
          <div key={m.key} className="border-l-4 border-slate-300 pl-3" data-testid={`metric-${m.key}`}>
            <dt className="text-xs text-slate-500 uppercase tracking-wide font-medium">{m.label}</dt>
            <dd className={`font-mono text-3xl md:text-4xl tracking-tighter ${m.tone} mt-1`} data-testid={`metric-${m.key}-value`}>
              {fmtMoney(m.value)}
            </dd>
          </div>
        ))}
      </dl>
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 5. Contractor utilisation heatmap
// ─────────────────────────────────────────────────────────────
function ContractorUtilisationWidget({ contractor }) {
  const rows = contractor?.rows || [];
  const days = contractor?.days || [];
  if (rows.length === 0) {
    return (
      <WidgetShell title="Contractor Utilisation · 4-week" icon={UsersFour} testid="widget-contractor-utilisation">
        <p className="text-sm text-slate-500">{contractor?.empty_reason || 'No contractors booked in the next 4 weeks.'}</p>
      </WidgetShell>
    );
  }
  const cellColor = (load) => load === 0 ? 'bg-slate-100'
    : load === 1 ? 'bg-blue-500'
    : 'bg-rose-500';
  return (
    <WidgetShell title="Contractor Utilisation · 4-week" icon={UsersFour} testid="widget-contractor-utilisation">
      <div className="overflow-x-auto -mx-2 px-2">
        <table className="text-xs" data-testid="contractor-heatmap">
          <thead>
            <tr>
              <th className="text-left pb-2 pr-3 font-medium text-slate-500 uppercase tracking-wide">Contractor</th>
              {days.map((d, i) => (
                <th key={d} className={`px-0.5 pb-1 font-mono text-[10px] text-slate-400 text-center ${i % 7 === 0 ? 'border-l border-slate-300' : ''}`}>
                  {new Date(d).getDate()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.contractor_id} data-testid={`contractor-row-${row.contractor_id}`}>
                <td className="pr-3 py-1 text-slate-700 whitespace-nowrap">
                  <span className="font-semibold">{row.name}</span>
                  {row.trade && <span className="text-slate-400 ml-1">· {row.trade}</span>}
                </td>
                {row.days.map((load, i) => (
                  <td key={i} className={`px-0.5 py-1 ${i % 7 === 0 ? 'border-l border-slate-300' : ''}`}>
                    <div className={`w-3 h-3 ${cellColor(load)}`} title={`${days[i]}: ${['Idle','Booked','Overbooked'][load]}`} data-testid={`heatmap-cell-${row.contractor_id}-${i}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-slate-100 border border-slate-300 inline-block" /> Idle</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 inline-block" /> Booked</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-rose-500 inline-block" /> Overbooked</span>
        </div>
      </div>
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 6. Franchise scorecard
// ─────────────────────────────────────────────────────────────
function FranchiseScorecardWidget({ scorecard }) {
  const criteria = scorecard?.criteria || [];
  const pct = scorecard?.readiness_pct ?? 0;
  return (
    <WidgetShell
      title="Franchise Readiness"
      icon={Trophy}
      testid="widget-franchise-scorecard"
      actionsRight={<span className="font-mono text-sm text-slate-900" data-testid="scorecard-readiness-pct">{pct}%</span>}
    >
      <div className="w-full h-2 bg-slate-100 mb-4">
        <div className="h-full bg-emerald-600" style={{ width: `${pct}%` }} data-testid="scorecard-progress-bar" />
      </div>
      <ul className="space-y-1 text-sm" data-testid="scorecard-criteria-list">
        {criteria.map((c) => (
          <li key={c.key} className="flex items-center justify-between gap-3 py-1 border-b border-slate-100 last:border-0" data-testid={`scorecard-item-${c.key}`}>
            <span className={c.met ? 'text-slate-400 line-through' : 'text-slate-800'}>{c.label}</span>
            <span className={`font-mono text-xs ${c.met ? 'text-emerald-600' : 'text-slate-500'}`}>{c.value}</span>
          </li>
        ))}
      </ul>
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 7 + 8. Job-cost variance and Stalled jobs (compact list widgets)
// ─────────────────────────────────────────────────────────────
function JobCostVarianceWidget({ variance }) {
  const flags = variance?.flags || [];
  const navigate = useNavigate();
  return (
    <WidgetShell
      title="Job-Cost Variance"
      icon={Gauge}
      testid="widget-variance"
      actionsRight={<span className="text-xs font-mono text-slate-500" data-testid="variance-count">{flags.length}</span>}
    >
      {flags.length === 0 ? (
        <p className="text-sm text-emerald-700 border-l-4 border-emerald-500 pl-3">All active jobs within 110% of quote.</p>
      ) : (
        <ul className="divide-y divide-slate-200" data-testid="variance-list">
          {flags.slice(0, 5).map((f) => {
            const s = SIGNAL[f.severity] || SIGNAL.warning;
            return (
              <li key={f.appraisal_id} className={`flex items-center justify-between py-2 border-l-4 ${s.border} pl-3`}>
                <button onClick={() => navigate(`/appraisals/${f.appraisal_id}`)} className="flex-1 text-left" data-testid={`variance-item-${f.appraisal_id}`}>
                  <div className="text-sm font-semibold text-slate-800">Job #{f.appraisal_no}</div>
                  <div className="text-xs text-slate-500 font-mono">
                    Actual {fmtMoney(f.actual)} · Quoted {fmtMoney(f.quoted)}
                  </div>
                </button>
                <span className={`font-mono text-lg ${s.text}`}>{f.variance_pct}%</span>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetShell>
  );
}

function StalledJobsWidget({ stalled }) {
  const flags = stalled?.flags || [];
  const navigate = useNavigate();
  return (
    <WidgetShell
      title="Stalled Jobs"
      icon={ClockCounterClockwise}
      testid="widget-stalled"
      actionsRight={<span className="text-xs font-mono text-slate-500" data-testid="stalled-count">{flags.length}</span>}
    >
      {flags.length === 0 ? (
        <p className="text-sm text-emerald-700 border-l-4 border-emerald-500 pl-3">Every WIP job has activity in the last 7 days.</p>
      ) : (
        <ul className="divide-y divide-slate-200" data-testid="stalled-list">
          {flags.slice(0, 5).map((f) => {
            const s = SIGNAL[f.severity] || SIGNAL.warning;
            return (
              <li key={f.appraisal_id} className={`flex items-center justify-between py-2 border-l-4 ${s.border} pl-3`}>
                <button onClick={() => navigate(`/appraisals/${f.appraisal_id}`)} className="flex-1 text-left" data-testid={`stalled-item-${f.appraisal_id}`}>
                  <div className="text-sm font-semibold text-slate-800">Job #{f.appraisal_no}</div>
                  <div className="text-xs text-slate-500">Last event: {f.last_event_at ? new Date(f.last_event_at).toLocaleDateString() : 'never'}</div>
                </button>
                <span className={`font-mono text-lg ${s.text}`}>{f.days_stalled}d</span>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 9. Revenue momentum
// ─────────────────────────────────────────────────────────────
function RevenueMomentumWidget({ momentum }) {
  const trend = momentum?.trend || 'flat';
  const TrendIcon = trend === 'up' ? ArrowUpRight : trend === 'down' ? ArrowDownRight : Minus;
  const trendTone = trend === 'up' ? 'text-emerald-700' : trend === 'down' ? 'text-rose-700' : 'text-slate-500';
  return (
    <WidgetShell title="Revenue Momentum" icon={Pulse} testid="widget-revenue-momentum">
      <div className="border-l-4 border-slate-300 pl-4">
        <div className="text-xs text-slate-500 uppercase tracking-wide">{momentum?.this_label}</div>
        <div className="font-mono text-4xl md:text-5xl tracking-tighter text-slate-900 mt-1" data-testid="momentum-this-month">
          {fmtMoney(momentum?.this_month)}
        </div>
        <div className={`flex items-center gap-2 mt-2 ${trendTone}`}>
          <TrendIcon size={18} weight="bold" />
          <span className="font-mono text-sm" data-testid="momentum-delta">
            {momentum?.delta != null ? fmtMoney(momentum.delta) : '—'}
            {momentum?.delta_pct != null && ` (${momentum.delta_pct > 0 ? '+' : ''}${momentum.delta_pct}%)`}
          </span>
          <span className="text-xs text-slate-400 ml-1">vs {momentum?.last_label}</span>
        </div>
      </div>
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Phase 2D · Return-Visits Due This Week
// ─────────────────────────────────────────────────────────────
function ReturnVisitsWidget({ data }) {
  const items = data?.items || [];
  const count = data?.count || 0;
  const overdue = data?.overdue_count || 0;
  return (
    <WidgetShell
      title="Return Visits · this week"
      icon={Timer}
      testid="widget-return-visits"
      actionsRight={
        <span className={`font-mono text-xs px-2 py-0.5 uppercase tracking-wider ${overdue > 0 ? 'bg-rose-600 text-white' : 'bg-slate-200 text-slate-700'}`}
              data-testid="return-visits-count">
          {count} due · {overdue} overdue
        </span>
      }
    >
      {items.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-4 text-center border border-dashed border-slate-200">
          No cure gaps ending this week. Every follow-up already booked.
        </div>
      ) : (
        <ul className="space-y-1 max-h-56 overflow-auto"
            data-testid="return-visits-list">
          {items.map((i) => (
            <li key={i.event_id + '-' + i.session_no}
                className={`border-l-4 ${i.overdue ? 'border-l-rose-600 bg-rose-50' : 'border-l-violet-500 bg-violet-50'} px-3 py-1.5 text-xs`}
                data-testid={`return-visits-item-${i.event_id}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold">
                  #{i.session_no}
                </span>
                <span className="text-slate-700">
                  {i.cure_reason?.replace(/_/g, ' ') || 'cure'}
                </span>
                <span className={`ml-auto font-mono ${i.overdue ? 'text-rose-700 font-bold' : 'text-slate-500'}`}>
                  {i.cure_end_date}{i.overdue && ' · overdue'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Phase 2D · Schedule Drift (top 5)
// ─────────────────────────────────────────────────────────────
function ScheduleDriftWidget({ data }) {
  const items = data?.items || [];
  return (
    <WidgetShell
      title="Schedule Drift · top 5 · 28d"
      icon={ArrowsSplit}
      testid="widget-schedule-drift"
      actionsRight={
        <span className="font-mono text-xs px-2 py-0.5 uppercase tracking-wider bg-slate-200 text-slate-700"
              data-testid="drift-considered">
          {data?.count_considered ?? 0} scanned · {data?.count_with_drift ?? 0} drift
        </span>
      }
    >
      {items.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-4 text-center border border-dashed border-slate-200">
          No variance yet. Waiting for foreman logs.
        </div>
      ) : (
        <ul className="space-y-1" data-testid="drift-list">
          {items.map((i, idx) => {
            const bad = i.hours_delta > 0;
            return (
              <li key={i.event_id}
                  className={`border-l-4 ${bad ? 'border-l-rose-500 bg-rose-50' : 'border-l-emerald-500 bg-emerald-50'} px-3 py-1.5`}
                  data-testid={`drift-item-${i.event_id}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-mono font-bold text-slate-500 w-4">
                    #{idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      A{i.appraisal_no || i.appraisal_id?.slice(-4)} · {i.client_name || '—'}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono truncate">
                      {i.contractor_name || '—'} · {i.start_date}
                    </div>
                  </div>
                  <div className="text-right font-mono">
                    <div className={`text-sm font-bold ${bad ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {i.hours_delta > 0 ? '+' : ''}{i.hours_delta}h
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {i.planned_hours}h → {i.actual_hours}h
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Phase 2D · Contractor Rate Leaderboard
// ─────────────────────────────────────────────────────────────
function RateLeaderboardWidget({ data }) {
  const boards = data?.boards || [];
  return (
    <WidgetShell
      title="Rate Leaderboard"
      icon={Medal}
      testid="widget-rate-leaderboard"
      actionsRight={
        <span className="font-mono text-xs px-2 py-0.5 uppercase tracking-wider bg-slate-200 text-slate-700">
          {data?.count_keys_ranked ?? 0} operations
        </span>
      }
    >
      {boards.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-4 text-center border border-dashed border-slate-200"
             data-testid="leaderboard-empty">
          {data?.empty_reason || 'Waiting for calibrator data.'}
        </div>
      ) : (
        <div className="space-y-3 max-h-64 overflow-auto"
             data-testid="leaderboard-list">
          {boards.map((b) => (
            <div key={b.work_rate_key}
                 className="border border-slate-200"
                 data-testid={`leaderboard-board-${b.work_rate_key}`}>
              <div className="bg-slate-100 px-2 py-1 flex items-center gap-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-700 truncate">
                  {b.work_rate_key}
                </span>
                <span className="ml-auto text-[9px] text-slate-500 font-mono">
                  {b.total_ranked} ranked
                </span>
              </div>
              <ul>
                {b.top.map((t, idx) => {
                  const fast = t.speed_multiplier > 1;
                  return (
                    <li key={t.contractor_id}
                        className="flex items-center gap-2 px-2 py-1 text-xs border-t border-slate-100">
                      <span className="font-mono font-bold text-slate-500 w-4">
                        {idx + 1}
                      </span>
                      <span className="flex-1 truncate text-slate-800">{t.name}</span>
                      <span className={`font-mono font-bold ${fast ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {t.speed_multiplier.toFixed(2)}×
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">
                        n={t.samples}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Phase 2D · Admin Review Queue tile
// ─────────────────────────────────────────────────────────────
function AdminReviewQueueTile({ data }) {
  const total = data?.total ?? 0;
  const byKind = data?.by_kind || {};
  const oldest = data?.oldest_pending_at
    ? new Date(data.oldest_pending_at).toLocaleDateString('en-NZ')
    : null;
  return (
    <WidgetShell
      title="Admin Review Queue"
      icon={ClipboardText}
      testid="widget-admin-review-queue"
    >
      <a href="/proposals"
         className="block border-l-4 border-slate-900 bg-slate-50 hover:bg-slate-100 p-3"
         data-testid="admin-review-queue-link">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-4xl font-bold text-slate-900"
                data-testid="admin-review-queue-total">
            {total}
          </span>
          <span className="text-xs uppercase tracking-wider text-slate-500">
            pending
          </span>
        </div>
        {total > 0 && (
          <>
            <ul className="mt-2 space-y-0.5 text-[11px] font-mono text-slate-700"
                data-testid="admin-review-queue-breakdown">
              {Object.entries(byKind).map(([k, c]) => (
                <li key={k} className="flex items-center gap-2">
                  <span className="truncate">{k.replace(/_/g, ' ')}</span>
                  <span className="ml-auto font-bold">{c}</span>
                </li>
              ))}
            </ul>
            {oldest && (
              <div className="mt-2 text-[10px] uppercase tracking-wider text-amber-700">
                Oldest since {oldest}
              </div>
            )}
          </>
        )}
        {total === 0 && (
          <div className="mt-2 text-xs text-slate-500 italic">
            Nothing awaiting review.
          </div>
        )}
      </a>
    </WidgetShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Stale data banner (bonus signal from design guidelines)
// ─────────────────────────────────────────────────────────────
function StaleDataBanner({ stale_data_warning }) {
  if (!stale_data_warning?.stale) return null;
  const last = stale_data_warning.last_event_at ? new Date(stale_data_warning.last_event_at).toLocaleString() : 'never';
  return (
    <div className={`border-l-4 ${SIGNAL.warning.border} ${SIGNAL.warning.bg} p-3 mb-4`} data-testid="stale-data-banner">
      <div className="flex items-center gap-2">
        <Warning size={16} weight="bold" className="text-amber-600" />
        <span className="text-sm font-semibold text-amber-900">
          No workflow activity in the last {stale_data_warning.threshold_hours}h — signals below may be stale.
        </span>
        <span className="text-xs text-amber-700 font-mono ml-auto">Last event: {last}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────
export default function DashboardOverviewPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/dashboard/overview');
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-500" data-testid="dashboard-overview-loading">
        Loading command centre…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="dashboard-overview-error">
        <div className={`border-l-4 ${SIGNAL.critical.border} ${SIGNAL.critical.bg} p-6 max-w-lg`}>
          <div className="text-rose-900 font-semibold">Dashboard unavailable</div>
          <div className="text-sm text-rose-800 mt-2">{error}</div>
          <button onClick={load} className="mt-3 text-xs uppercase tracking-wide font-bold border border-rose-500 text-rose-900 px-3 py-1 hover:bg-rose-100" data-testid="dashboard-overview-retry">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="dashboard-overview-page">
      {/* Command header */}
      <header className="sticky top-0 z-20 bg-slate-900 text-white border-b border-slate-700">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tighter" style={{ fontFamily: "'Chivo', sans-serif" }} data-testid="dashboard-title">
              COMMAND CENTRE
            </h1>
            <p className="text-xs text-slate-400 uppercase tracking-[0.2em] mt-1 font-mono" data-testid="dashboard-generated-at">
              Generated {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString('en-NZ') : '—'}
            </p>
          </div>
          <button
            onClick={load}
            className="text-xs uppercase tracking-wide font-bold border border-slate-500 text-white px-3 py-2 hover:bg-slate-800 flex items-center gap-2 transition-none"
            data-testid="dashboard-refresh"
          >
            <ArrowsClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-[1600px] mx-auto p-4 md:p-6">
        <StaleDataBanner stale_data_warning={data?.stale_data_warning} />

        <div className="grid grid-cols-12 gap-4">
          {/* Row 1 — headline capacity + revenue */}
          <div className="col-span-12 lg:col-span-4">
            <CapacityWidget capacity={data?.capacity} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <RevenueMomentumWidget momentum={data?.revenue_momentum} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <FranchiseScorecardWidget scorecard={data?.franchise_scorecard} />
          </div>

          {/* Row 2 — pipeline (wide) + bottlenecks (tall right column) */}
          <div className="col-span-12 lg:col-span-8">
            <PipelineWidget pipeline={data?.pipeline} />
          </div>
          <div className="col-span-12 lg:col-span-4 row-span-2">
            <BottleneckAlertsWidget bottlenecks={data?.bottlenecks} />
          </div>

          {/* Row 3 — cashflow spans wide under pipeline */}
          <div className="col-span-12 lg:col-span-8">
            <CashflowWidget cashflow={data?.cashflow} />
          </div>

          {/* Row 4 — variance / stalled / utilisation */}
          <div className="col-span-12 lg:col-span-4">
            <JobCostVarianceWidget variance={data?.job_cost_variance} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <StalledJobsWidget stalled={data?.stalled_jobs} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <WidgetShell title="Ops Toolkit" icon={Hammer} testid="widget-ops-toolkit">
              <ul className="space-y-1 text-sm">
                <li><a href="/pipeline" className="text-slate-800 hover:text-slate-900 hover:underline" data-testid="link-pipeline">→ Pipeline Hub</a></li>
                <li><a href="/appraisals" className="text-slate-800 hover:text-slate-900 hover:underline" data-testid="link-appraisals">→ Appraisals</a></li>
                <li><a href="/calendar" className="text-slate-800 hover:text-slate-900 hover:underline" data-testid="link-calendar">→ Calendar</a></li>
                <li><a href="/leads" className="text-slate-800 hover:text-slate-900 hover:underline" data-testid="link-leads">→ Leads</a></li>
              </ul>
            </WidgetShell>
          </div>

          {/* Row 5 — full-width utilisation heatmap */}
          <div className="col-span-12">
            <ContractorUtilisationWidget contractor={data?.contractor_utilisation} />
          </div>

          {/* Row 6 — Phase 2D scheduling wire-ups */}
          <div className="col-span-12 lg:col-span-4">
            <ScheduleDriftWidget data={data?.schedule_drift} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <ReturnVisitsWidget data={data?.return_visits_due} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <AdminReviewQueueTile data={data?.admin_review_queue} />
          </div>

          {/* Row 7 — full-width leaderboard */}
          <div className="col-span-12">
            <RateLeaderboardWidget data={data?.rate_leaderboard} />
          </div>
        </div>
      </main>
    </div>
  );
}
